import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import {
  api,
  type DataProviderConfigField,
  type DataProviderTestResult,
  type EmailProvider,
} from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import AccordionCard from "../../../components/AccordionCard.tsx";
import { AdvancedFold } from "../../../components/FoldList.tsx";
import SecretCredentialForm from "../../../components/SecretCredentialForm.tsx";
import type { Status } from "../../../components/StatusIcon.tsx";
import { withoutWhitespace } from "../../../utils/secrets.ts";

function emailStatus(provider: EmailProvider): Status {
  if (!provider.secret_stored) return { kind: "attention", label: "No key" };
  if (!provider.enabled) return { kind: "off", label: "Disabled" };
  return { kind: "ok", label: "Enabled" };
}

function EmailSettingsForm({ provider }: { provider: EmailProvider }) {
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(provider.enabled);
  const [secret, setSecret] = useState("");
  const [config, setConfig] = useState<Record<string, string>>(provider.config);
  const [testResult, setTestResult] = useState<DataProviderTestResult | null>(null);
  const secretField = provider.config_fields.find((f) => f.secret);
  const advancedFields = provider.config_fields.filter((f) => f.advanced && !f.secret);
  const overridden = advancedFields.filter((f) => provider.config[f.name]);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: keys.dataProvider.email() });

  const save = useMutation({
    mutationFn: () => api.updateEmailProvider({ enabled, secret: withoutWhitespace(secret) || null, config }),
    onSuccess: () => {
      setSecret("");
      setTestResult(null);
      invalidate();
      toast.success("Email provider saved");
    },
  });
  const clear = useMutation({
    mutationFn: api.clearEmailProviderSecret,
    onSuccess: () => {
      setEnabled(false);
      setTestResult(null);
      invalidate();
      toast.success("Email provider key cleared");
    },
  });
  const test = useMutation({ mutationFn: api.testEmailProvider, onSuccess: setTestResult });
  const testSend = useMutation({
    mutationFn: api.sendTestEmail,
    onSuccess: (r) => toast.success(`Test email queued to ${r.to}`),
  });

  const renderField = (field: DataProviderConfigField) => (
    <TextField
      key={field.name}
      label={field.label}
      value={config[field.name] ?? ""}
      onChange={(e) => setConfig((c) => ({ ...c, [field.name]: e.target.value }))}
      placeholder={field.default}
      helperText={[field.help_text, field.default && `Default: ${field.default}`].filter(Boolean).join(" ") || " "}
      fullWidth
      size="small"
    />
  );

  return (
    <Stack spacing={2}>
      <SecretCredentialForm
        name={secretField?.label ?? "API secret key"}
        stored={provider.secret_stored}
        hint={provider.secret_hint}
        updatedAt={provider.updated_at}
        updatedByEmail={provider.updated_by_email}
        encryptionConfigured={provider.encryption_configured}
        encryptionWarning="saving a key is refused rather than storing it in clear text."
        value={secret}
        onChange={setSecret}
        fieldLabel={secretField?.label ?? "API secret key"}
        placeholder={provider.secret_stored ? `Stored ••••${provider.secret_hint}` : ""}
        helperText={
          provider.secret_stored
            ? "Write-only. Leave blank to keep the stored key."
            : secretField?.help_text || "Write-only — never shown again."
        }
        onTest={() => test.mutate()}
        testing={test.isPending}
        testDisabled={!provider.secret_stored}
        testLabel="Test connection"
        testResult={
          testResult && {
            ok: testResult.ok,
            detail: (
              <>
                {testResult.detail}
                {testResult.info.details?.length ? (
                  <> Domains: {testResult.info.details.map((d) => `${d.label} (${d.value})`).join(" · ")}.</>
                ) : null}
              </>
            ),
          }
        }
        onClear={() => clear.mutateAsync()}
        clearLabel="Clear key"
        clearConfirm={{
          title: "Clear email provider key",
          body: <>Clear the stored API key? No email is sent until a new one is saved.</>,
        }}
      />
      <FormControlLabel
        control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
        label="Enabled for sending"
      />
      {provider.config_fields.filter((f) => !f.advanced && !f.secret).map(renderField)}
      {advancedFields.length > 0 && (
        <AdvancedFold
          summary={overridden.length ? `${overridden.map((f) => f.label).join(", ")} changed` : "Defaults"}
        >
          <Stack spacing={2} sx={{ pt: 1 }}>
            {advancedFields.map(renderField)}
          </Stack>
        </AdvancedFold>
      )}
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          onClick={() => save.mutate()}
          loading={save.isPending}
          disabled={!!withoutWhitespace(secret) && !provider.encryption_configured}
        >
          Save
        </Button>
        <Button onClick={() => testSend.mutate()} loading={testSend.isPending} disabled={!provider.enabled}>
          Send test email to me
        </Button>
      </Stack>
    </Stack>
  );
}

/**
 * The platform's email account (Scaleway Transactional Email, EU): invitations
 * and other platform email go out through it. Delivery is on the platform page.
 */
export default function EmailProviderCard() {
  const provider = useQuery({ queryKey: keys.dataProvider.email(), queryFn: api.getEmailProvider });
  const p = provider.data;
  if (!p) return null;
  const status = emailStatus(p);
  const sender = p.config.from_email || p.config_fields.find((f) => f.name === "from_email")?.default;
  return (
    <AccordionCard
      title={p.name}
      summary={[
        "Email",
        p.secret_stored ? `API key …${p.secret_hint}` : "No API key stored",
        ...(sender ? [`Sends as ${sender}`] : []),
      ].join(" · ")}
      status={status}
      defaultExpanded={status.kind === "attention"}
    >
      <Stack spacing={2}>
        <Box>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            The sender's domain must be verified in Scaleway (SPF, DKIM, MX and DMARC records) before
            email goes out; Test connection checks it.
          </Typography>
        </Box>
        {/* Remounts after a save, so the form shows what was stored. */}
        <EmailSettingsForm key={p.updated_at ?? ""} provider={p} />
      </Stack>
    </AccordionCard>
  );
}
